import { getMaxColumns } from '../../../../shared/constants/formLayout';
import {
  type Field,
  type FieldElement,
  type Row,
  type Section,
} from '../../../../shared/types';
import { isRow, isSection } from '../../../../shared/utils';

import { deepClone, getAvailableColumns } from './dragAndDrop';

/**
 * Adjusts colSpan for password confirmation fields when isPasswordConfirmation changes
 */
function adjustPasswordConfirmationColSpan(
  field: FieldElement,
  originalField: FieldElement
): FieldElement {
  // Only process password fields
  if (
    !('fieldType' in field) ||
    field.fieldType !== 'password' ||
    !('colSpan' in field)
  ) {
    return field;
  }

  const passwordField = field as Field & {
    fieldType: 'password';
    advConfig: { isPasswordConfirmation: boolean };
    colSpan: number;
  };
  const originalPasswordField = originalField as Field & {
    fieldType: 'password';
    advConfig: { isPasswordConfirmation: boolean };
    colSpan: number;
  };

  // Check if isPasswordConfirmation changed from false to true
  const wasConfirmationDisabled =
    !originalPasswordField.advConfig?.isPasswordConfirmation;
  const isConfirmationEnabled = passwordField.advConfig?.isPasswordConfirmation;

  if (wasConfirmationDisabled && isConfirmationEnabled) {
    // Adjust colSpan: 1 -> 2, 3 -> 4, keep 2 and 4 as is
    const currentColSpan = passwordField.colSpan;
    let newColSpan = currentColSpan;

    if (currentColSpan === 1) {
      newColSpan = 2;
    } else if (currentColSpan === 3) {
      newColSpan = 4;
    }

    return { ...passwordField, colSpan: newColSpan };
  }

  return field;
}

function rebalanceRow(row: Row): Row[] {
  const overflowedFields: Field[] = [];
  const currentFields: Field[] = [];

  let usedCols = 0;

  const maxColumns = getMaxColumns();

  row.children?.reduce((acc: Field[], field: Field) => {
    const colSpan = field.colSpan || 1;

    if (usedCols + colSpan > maxColumns) {
      overflowedFields.push(field);
    } else {
      currentFields.push(field);
      usedCols += colSpan;
    }

    return acc;
  }, []);

  const newRow: Row = {
    ...row,
    children: currentFields,
    availableColumns: maxColumns - usedCols,
  };

  if (overflowedFields.length === 0) {
    return [newRow];
  }

  // Calculate used columns for overflow row
  const overflowUsedCols = overflowedFields.reduce(
    (sum, field) => sum + (field.colSpan || 1),
    0
  );

  const overflowRow: Row = {
    id: `row-${Date.now()}`,
    type: 'row',
    label: '',
    fieldName: '',
    children: overflowedFields,
    availableColumns: maxColumns - overflowUsedCols,
  };

  return [newRow, overflowRow];
}

/* ------------------------------------------------------------------
 *  Core: rebalanceRowsAfterColSpanChange
 * ------------------------------------------------------------------
 * Ensures invariant #1 for a single row + cascades overflow into
 * successive rows.  Order of fields is preserved.
 */
export function rebalanceRowsAfterColSpanChange(
  canvas: FieldElement[],
  rowId: string
): FieldElement[] {
  return canvas.flatMap((item) => {
    if (isRow(item) && item.id === rowId) {
      return rebalanceRow(item);
    }

    if (isSection(item) && Array.isArray(item.children)) {
      const updatedChildren = item.children.flatMap((child) => {
        if (isRow(child) && child.id === rowId) {
          return rebalanceRow(child);
        }

        return child;
      });

      return { ...item, children: updatedChildren } as Section;
    }

    return item;
  });
}

/* ------------------------------------------------------------------
 *  Public util – updateFieldColSpan (resizes then re‑balances)
 * ----------------------------------------------------------------*/

export function updateFieldColSpan(
  canvas: FieldElement[],
  rowId: string,
  fieldId: string,
  newColSpan: number
): FieldElement[] {
  const cloned = deepClone(canvas);

  const updated = cloned.map((item) => {
    // Top-level row
    if (isRow(item) && item.id === rowId && item.children) {
      const updatedChildren = item.children.map((child) =>
        child.id === fieldId ? { ...child, colSpan: newColSpan } : child
      );

      return { ...item, children: updatedChildren } as Row;
    }

    // Section with rows
    if (isSection(item) && Array.isArray(item.children)) {
      const updatedSectionChildren = item.children.map((child) => {
        if (isRow(child) && child.id === rowId && child.children) {
          const updatedChildren = child.children.map((c) =>
            c.id === fieldId ? { ...c, colSpan: newColSpan } : c
          );

          return { ...child, children: updatedChildren } as Row;
        }

        return child;
      });

      return { ...item, children: updatedSectionChildren } as Section;
    }

    return item;
  });

  return rebalanceRowsAfterColSpanChange(updated, rowId);
}

export function refreshRowCapacities(fields: FieldElement[]): FieldElement[] {
  return fields.map((item) => {
    if (isRow(item)) {
      return {
        ...item,
        availableColumns: getAvailableColumns(item),
      } as Row;
    }

    if (isSection(item) && Array.isArray(item.children)) {
      const updatedChildren = item.children.map((child) => {
        if (isRow(child)) {
          return {
            ...child,
            availableColumns: getAvailableColumns(child),
          } as Row;
        }

        return child;
      });

      return { ...item, children: updatedChildren } as Section;
    }

    return item;
  });
}

/**
 * Updates a field and handles password confirmation colSpan adjustments with rebalancing
 */
export function updateFieldWithPasswordColSpanAdjustment(
  canvas: FieldElement[],
  targetId: string,
  updatedField: FieldElement,
  originalField: FieldElement
): FieldElement[] {
  // First adjust colSpan if needed for password confirmation
  const adjustedField = adjustPasswordConfirmationColSpan(
    updatedField,
    originalField
  );

  // Find the row containing this field
  let targetRowId: string | null = null;

  const findRowId = (fields: FieldElement[]): void => {
    fields.forEach((item) => {
      if (
        isRow(item) &&
        item.children?.some((child) => child.id === targetId)
      ) {
        targetRowId = item.id;
      } else if (isSection(item) && item.children) {
        item.children.forEach((row) => {
          if (
            isRow(row) &&
            row.children?.some((child) => child.id === targetId)
          ) {
            targetRowId = row.id;
          }
        });
      }
    });
  };

  findRowId(canvas);

  // Update the field in canvas
  const updatedCanvas = canvas.map((field) => {
    if (field.id === targetId) {
      return adjustedField;
    }

    if (isSection(field) && field.children) {
      return {
        ...field,
        children: field.children.map((row) => {
          if (isRow(row) && row.children) {
            return {
              ...row,
              children: row.children.map((child) =>
                child.id === targetId ? adjustedField : child
              ),
            } as Row;
          }

          return row;
        }),
      } as Section;
    }

    if (isRow(field) && field.children) {
      return {
        ...field,
        children: field.children.map((child) =>
          child.id === targetId ? adjustedField : child
        ),
      } as Row;
    }

    return field;
  });

  // If colSpan changed and we found the row, rebalance it
  if (
    targetRowId &&
    'colSpan' in adjustedField &&
    'colSpan' in originalField &&
    adjustedField.colSpan !== originalField.colSpan
  ) {
    return rebalanceRowsAfterColSpanChange(updatedCanvas, targetRowId);
  }

  return refreshRowCapacities(updatedCanvas);
}
